import { Entity, randomBool, randomIntBetween } from "game-common/models"

export interface ScreenShakeState {
    isShaking: boolean
    shakeOffsetX: number
    shakeOffsetY: number
}

/**
 * Two independent shake channels, summed at read time.
 *
 *   THE HIT SHAKE (`screenShake`) is the original: a short, one-at-a-time jolt when the player takes
 *     damage. It steps its own offsets and zeroes them when it finishes.
 *   THE IMPULSE (`impulse`) is for world events the player did not get hit by -- an Ember Core blast
 *     going off nearby being the first. It is amplitude-and-duration driven and DECAYS, so a distant
 *     blast is a nudge and one at your feet is a wallop, out of the same call.
 *
 * They are separate because they mean different things and must be able to overlap: being hit while
 * a charge detonates should not silently drop either cue. Keeping the impulse out of the hit shake's
 * `isShaking` latch is the whole point of the split.
 */
export class ScreenShakeManager {
    private state: ScreenShakeState = {
        isShaking: false,
        shakeOffsetX: 0,
        shakeOffsetY: 0,
    }

    private impulseOffsetX = 0
    private impulseOffsetY = 0
    private impulseAmplitude = 0
    private impulseInterval: ReturnType<typeof setInterval> | undefined

    private readonly screenshakeEnabled: boolean = true

    /**
     * Get current shake state. Both channels are summed here so every caller sees the same camera
     * offset without having to know there are two of them.
     */
    public getState(): ScreenShakeState {
        return {
            isShaking: this.state.isShaking || !!this.impulseInterval,
            shakeOffsetX: this.state.shakeOffsetX + this.impulseOffsetX,
            shakeOffsetY: this.state.shakeOffsetY + this.impulseOffsetY,
        }
    }

    /**
     * Update shake offsets
     */
    public setShakeOffsets(x: number, y: number): void {
        this.state.shakeOffsetX = x
        this.state.shakeOffsetY = y
    }

    /**
     * Reset shake state
     */
    public reset(): void {
        this.state.shakeOffsetX = 0
        this.state.shakeOffsetY = 0
        this.state.isShaking = false
        this.stopImpulse()
    }

    /** Room teardown and session end both go through reset(); nothing may outlive them. */
    private stopImpulse(): void {
        if (this.impulseInterval) {
            clearInterval(this.impulseInterval)
            this.impulseInterval = undefined
        }
        this.impulseOffsetX = 0
        this.impulseOffsetY = 0
        this.impulseAmplitude = 0
    }

    /**
     * A decaying camera kick. `amplitude` is the opening displacement in world pixels; it falls off
     * quadratically over `durationMs`, which is what makes it read as an impact rather than a
     * vibration.
     *
     * Two frequencies, deliberately incommensurate (about 14Hz across and 11Hz down), so the camera
     * traces a path that never repeats inside one kick -- a single frequency on both axes draws a
     * diagonal line and looks like a glitch. A new kick REPLACES a weaker one still running instead
     * of queueing: two blasts in a second should be one bigger shake, not a four-second rattle.
     */
    public impulse(entity: Entity, amplitude: number, durationMs: number, onUpdate: (entity: Entity) => void): void {
        if (!this.screenshakeEnabled || amplitude <= 0 || durationMs <= 0) {
            return
        }
        if (this.impulseInterval && amplitude <= this.impulseAmplitude) {
            return
        }
        this.stopImpulse()
        this.impulseAmplitude = amplitude
        const startedAt = Date.now()
        const phase = Math.random() * Math.PI * 2
        this.impulseInterval = setInterval(() => {
            const elapsed = Date.now() - startedAt
            if (elapsed >= durationMs) {
                this.stopImpulse()
                onUpdate(entity)
                return
            }
            const decay = (1 - elapsed / durationMs) ** 2
            this.impulseOffsetX = Math.sin(elapsed * 0.088 + phase) * amplitude * decay
            this.impulseOffsetY = Math.cos(elapsed * 0.071 + phase * 1.7) * amplitude * decay * 0.7
            onUpdate(entity)
        }, 16)
    }

    /**
     * Trigger screen shake effect
     */
    public screenShake(entity: Entity, onUpdate: (entity: Entity) => void): void {
        if (!this.screenshakeEnabled) {
            return
        }
        if (this.state.isShaking) {
            return
        }

        this.state.isShaking = true
        let startTs = Date.now()
        let shakeDirLeft = randomBool()
        let shakeDistance = randomIntBetween(5, 8)
        let currentShakeDistance = shakeDistance
        let duration = randomIntBetween(40, 60)
        let up: boolean = randomBool()

        const interval = setInterval(() => {
            let elapsed = Date.now() - startTs
            if (elapsed > duration) {
                clearInterval(interval)
                this.state.shakeOffsetX = 0
                this.state.shakeOffsetY = 0
                this.state.isShaking = false

                onUpdate(entity)
                return
            }

            if ((elapsed / duration) % 0.25 === 0 || currentShakeDistance <= 0) {
                shakeDirLeft = !shakeDirLeft
                shakeDistance = shakeDistance - 1
                currentShakeDistance = shakeDistance
            }

            currentShakeDistance -= 1
            this.state.shakeOffsetX += shakeDirLeft ? 1 : -1
            onUpdate(entity)

            this.state.shakeOffsetY += up ? -1 : 1
            up = !up
        }, 10)
    }
}
